import { Col, Row } from "antd";
import React, { useMemo } from "react";
import { FormFieldConfig } from "src/Library/Types";
import FormFieldRenderer from "./FormFieldRenderer";



type IProps = {
    formFields: FormFieldConfig[];
    gridRows: number;
    gridCols: number;
    children: React.ReactNode;
    gutter?: [number, number];
    labelMap?: Record<string, string>;
};



const FormGridContainer = ({
    formFields,
    gridRows,
    gridCols,
    children,
    gutter = [4,4],
    labelMap
}: IProps) => {

    const grid = useMemo(() => {

        if (!formFields || formFields.length === 0) return [];
        const newGrid = Array(gridRows).fill(null).map(() => Array(gridCols).fill(null));
       
        const sortedFields = [...formFields].sort((a, b) => {
            if ((a.posY ?? 0) !== (b.posY ?? 0)) return (a.posY ?? 0) - (b.posY ?? 0);
            return (a.posX ?? 0) - (b.posX ?? 0);
        })

        sortedFields.forEach(field => {
            const rowIndex = field.posY;
            const colIndex = field.posX;

            if (rowIndex != null && colIndex != null && rowIndex < gridRows && colIndex < gridCols) {
                 newGrid[rowIndex][colIndex] = field;
            } else {
                console.warn(`Field ${field.id} is out of bounds: (${rowIndex}, ${colIndex})`);
            }
            
        });

        return newGrid
    }, [formFields, gridRows, gridCols]);

    // Extract fields with hierarchical IDs (e.g., "grid.additionalDetails.movementType")
    // This is used to identify fields that are part of a nested structure.
    // It helps in rendering the fields correctly in the FormFieldRenderer.
    const extractedFields = useMemo(() => {
        const extracted = new Set<string>();
        if (!formFields || formFields.length === 0) return extracted;
        formFields.forEach(field => {
            if (field.id.includes('.')) {
                extracted.add(field.id);
            }
        });
        return extracted;
    }, [formFields]);

    return (
        <div>
            {grid.map((row, rowIndex) => (
                <Row 
                    key = {`row-${rowIndex}`}
                    style = {{
                        margin: 0, 
                        padding: 0,
                        width: '100%',
                    }}
                    gutter={gutter}
                >
                    {row.map((field, colIndex) => {
                        if (!field) {
                            return (
                                <Col 
                                    key={`col-${rowIndex}-${colIndex}`}
                                    style={{
                                        width: "10px"
                                    }}
                                >
                                {/* Empty Cell */}
                                </Col>
                            )
                        }

                        const colspan = Math.round((24 / gridCols) * (field.colspan || 1));
                        return (
                            <Col 
                            key={`col-${rowIndex}-${colIndex}`} 
                            span={colspan} 
                            style={{ 
                                margin: 0,
                                minWidth: 0,
                            }}
                            >
                                <div style={{ 
                                    width: '100%',
                                    minWidth: 0
                                }}>
                                    <FormFieldRenderer
                                        field={field}
                                        extractedFields={extractedFields}
                                        labelMap={labelMap}
                                    >
                                        {children}
                                    </FormFieldRenderer>
                                </div>
                            </Col>
                        )
                    })}
                </Row>
            ))}
        </div>
    )
}

export default FormGridContainer;